import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';

/**
 * LoginPage — 自定义登录 / 邀请码注册页（/login）
 *
 * 双栏布局：
 * - 左栏 45%：站点介绍 + 邀请码获取引导 + 微信客服二维码
 * - 右栏 55%：登录 / 注册表单（Tab 切换）
 *
 * 注册：POST /taro/redeem-and-register（邀请码 = 星币充值券码）
 * 登录：app.session.login()（Flarum 官方 session 登录，POST baseUrl/login）
 */
export default class LoginPage extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.activeTab = 'login';
    this.loading = false;
    this.error = null;
    this.success = null;

    this.username = '';
    this.password = '';
    this.inviteCode = '';
    this.email = '';
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    // 隐藏 Flarum 默认 header（登录页全屏双栏布局）
    document.body.classList.add('LoginPage-active');
  }

  onremove(vnode) {
    super.onremove(vnode);
    document.body.classList.remove('LoginPage-active');
  }

  view() {
    return (
      <div className="LoginPage">
        <div className="LoginPage-hero">
          <div className="LoginPage-logo">格林塔罗学员辅导论坛</div>
          <h1>用塔罗，读懂自己</h1>
          <p>
            格林塔罗学员辅导论坛是一个专注于塔罗牌学习与应用的社区。
            这里提供系统化的课程、案例讨论与 AI 导师辅导，帮助每一位学员建立自己的解读体系。
          </p>

          <div className="LoginPage-steps">
            <h3>如何加入我们？</h3>
            <ol>
              <li>添加微信客服（微信号见下方二维码）</li>
              <li>获取专属邀请码</li>
              <li>在右侧切换到「注册」，填写邀请码 + 账号信息</li>
              <li>开始你的塔罗学习之旅</li>
            </ol>
          </div>

          <div className="LoginPage-qr">
            <img src="/assets/wechat-qr.png" alt="微信客服二维码" />
            <p>扫码添加客服 · 获取邀请码</p>
          </div>
        </div>

        <div className="LoginPage-form">
          <div className="LoginPage-tabs">
            <button
              type="button"
              className={this.activeTab === 'login' ? 'active' : ''}
              onclick={() => { this.activeTab = 'login'; this.error = null; this.success = null; }}
            >
              登录
            </button>
            <button
              type="button"
              className={this.activeTab === 'register' ? 'active' : ''}
              onclick={() => { this.activeTab = 'register'; this.error = null; this.success = null; }}
            >
              注册
            </button>
          </div>

          {this.success ? (
            <div className="LoginPage-success Alert Alert--success">
              <i className="fas fa-check-circle" /> {this.success}
            </div>
          ) : null}

          {this.activeTab === 'login' ? this.loginForm() : this.registerForm()}
        </div>
      </div>
    );
  }

  loginForm() {
    return (
      <form onsubmit={this.handleLogin.bind(this)}>
        <input
          type="text"
          className="FormControl"
          placeholder="用户名"
          value={this.username}
          disabled={this.loading}
          oninput={(e) => { this.username = e.target.value; }}
        />
        <input
          type="password"
          className="FormControl"
          placeholder="密码"
          value={this.password}
          disabled={this.loading}
          oninput={(e) => { this.password = e.target.value; }}
        />
        <button type="submit" disabled={this.loading || !this.username || !this.password}>
          {this.loading ? '登录中…' : '登录'}
        </button>
        {this.error ? <div className="LoginPage-error">{this.error}</div> : null}
      </form>
    );
  }

  registerForm() {
    return (
      <form onsubmit={this.handleRegister.bind(this)}>
        <input
          type="text"
          className="FormControl LoginPage-inviteInput"
          placeholder="邀请码（必填，向微信客服获取）"
          value={this.inviteCode}
          disabled={this.loading}
          oninput={(e) => { this.inviteCode = e.target.value; }}
        />
        <input
          type="text"
          className="FormControl"
          placeholder="用户名"
          value={this.username}
          disabled={this.loading}
          oninput={(e) => { this.username = e.target.value; }}
        />
        <input
          type="email"
          className="FormControl"
          placeholder="邮箱"
          value={this.email}
          disabled={this.loading}
          oninput={(e) => { this.email = e.target.value; }}
        />
        <input
          type="password"
          className="FormControl"
          placeholder="密码（至少 8 位）"
          value={this.password}
          disabled={this.loading}
          oninput={(e) => { this.password = e.target.value; }}
        />
        <button type="submit" disabled={this.loading || !this.inviteCode || !this.username || !this.email || !this.password}>
          {this.loading ? '注册中…' : '注册并激活'}
        </button>
        {this.error ? <div className="LoginPage-error">{this.error}</div> : null}
        <p className="LoginPage-hint">
          邀请码即学员专属充值券码，注册成功后对应积分将自动充值到你的账户。
        </p>
      </form>
    );
  }

  /**
   * 登录/注册成功后的落地跳转：SPA 导航到「我的案例」
   *
   * - POST /login 成功后 Set-Cookie 已写入，但 app.session.user 仍是 guest
   *   （要等下次 boot 的 preloaded payload）。
   * - 手动从 store 取/建 user 并更新 app.session.user → guest 检查通过，
   *   m.route.set 即可无刷新跳转（保留前端 state）。
   * - 兜底：SPA 跳转失败（如路由异常）→ 退回原生导航（全量刷新）。
   */
  _redirectToMyCases(username) {
    const target = '/u/' + encodeURIComponent(username) + '/cases';
    try {
      // 拉取用户数据进 store（guest session 下 app.store.find 也可以）
      app
        .request({ method: 'GET', url: app.forum.attribute('apiUrl') + '/users/' + encodeURIComponent(username) })
        .then((res) => {
          try {
            if (res && res.data) {
              app.store.pushPayload({ data: res.data });
              const user = app.store.getById('users', String(res.data.id));
              if (user && app.session) {
                app.session.user = user;
              }
            }
          } catch (_) {}
          m.route.set(target);
        })
        .catch(() => {
          // 拉取失败也直接跳（强制登录逻辑此时 session 已有效）
          m.route.set(target);
        });
    } catch (_) {
      window.location.href = window.location.origin + target;
    }
    // 5s 后仍在登录页 → 强制原生跳转兜底
    setTimeout(() => {
      if (document.querySelector('.LoginPage')) {
        window.location.href = window.location.origin + target;
      }
    }, 5000);
  }

  /**
   * Flarum session 登录：POST /login（与 LogInModal 完全相同的请求格式）
   * 成功 → Set-Cookie → reload 生效
   */
  async _flarumLogin(identification, password) {
    const csrfToken = (app.session && app.session.csrfToken) || '';
    const baseUrl = app.forum.attribute('baseUrl') || '';
    const res = await fetch(baseUrl + '/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        identification: identification,
        password: password,
        remember: true,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      const msg = data.errors && data.errors[0] && data.errors[0].detail;
      throw new Error(msg || '登录失败');
    }
  }

  handleLogin(e) {
    e.preventDefault();
    if (this.loading) return;
    this.loading = true;
    this.error = null;

    this._flarumLogin(this.username.trim(), this.password)
      .then(() => {
        // 登录成功：SPA 导航到「我的案例」（m.route.set，保留前端 state）
        this._redirectToMyCases(this.username.trim());
      })
      .catch((err) => {
        this.loading = false;
        this.error = err.message || '用户名或密码错误';
        m.redraw();
      });
  }

  handleRegister(e) {
    e.preventDefault();
    if (this.loading) return;
    this.loading = true;
    this.error = null;
    this.success = null;

    const username = this.username.trim();
    const email = this.email.trim();
    const password = this.password;

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/redeem-and-register',
        body: {
          invite_code: this.inviteCode.trim(),
          username: username,
          email: email,
          password: password,
        },
      })
      .then(() => {
        // 注册成功：自动登录（同一机制，POST /login）
        return this._flarumLogin(username, password);
      })
      .then(() => {
        // 注册成功：SPA 导航到「我的案例」
        this._redirectToMyCases(username);
      })
      .catch((err) => {
        this.loading = false;
        this.error =
          (err.message) ||
          '注册失败，请检查填写信息';
        m.redraw();
      });
  }
}
